// src/features/orders/ordersSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

interface OrderStatusHistory {
  id: string;
  status: string;
  details: string;
  comment: string | null;
  createdAt: string;
  awbNumber: string;
  warehouseName: string;
  userName: string;
  userType: string;
  orderId: string;
}

interface OrderState {
  list: any[];
  statusHistory: Record<string, OrderStatusHistory[]>;
  loading: boolean;
  error: string | null;
}

interface Order {
  id: string;
  status: string;
  [key: string]: any;
}

const initialState: OrderState = {
  list: [],
  statusHistory: {},
  loading: false,
  error: null,
};

const ordersSlice = createSlice({
  name: "orders",
  initialState,
  reducers: {
    setOrdersList(state, action: PayloadAction<any[]>) {
      state.list = action.payload;
      state.loading = false;
    },

    setOrderHistory(
      state,
      action: PayloadAction<{ orderId: string; history: OrderStatusHistory[] }>
    ) {
      state.statusHistory[action.payload.orderId] = action.payload.history;
    },

    addSingleStatusUpdate(
      state: any,
      action: PayloadAction<OrderStatusHistory>
    ) {
      const { orderId } = action.payload;
      if (!orderId) {
        console.error(
          "❌ AddStatusUpdate: orderId is missing from the payload",
          action.payload
        );
        return;
      }

      if (
        !state.statusHistory[orderId] ||
        !Array.isArray(state.statusHistory[orderId])
      ) {
        state.statusHistory[orderId] = [];
      }

      state.statusHistory[orderId].push(action.payload);
      if (state.list && state.list.orders && Array.isArray(state.list.orders)) {
        const orderInList = state.list.orders.find(
          (o: any) => o.id === orderId
        );
        if (orderInList) {
          orderInList.status = action.payload.status;
        }
      }
    },
    addNewOrder(state: any, action: PayloadAction<Order>) {
      const newOrder = action.payload;
      const existingOrder = state.list.orders.find(
        (order: any) => order.id === newOrder.id
      );
      if (!existingOrder) {
        state.list.orders.unshift(newOrder);

        if (state.list.length > 10) {
          state.list.orders.pop();
        }
      }
    },
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
    },
  },
});

export const {
  setOrdersList,
  setOrderHistory,
  addSingleStatusUpdate,
  addNewOrder,
  setLoading,
  setError,
} = ordersSlice.actions;

export default ordersSlice.reducer;
